<template>
  <div class="evollu-form state-machine">

    <h3>{{ stateMachine.name }}</h3>

    <div v-if="isLoading" class="my-4 py-4 d-flex flex-column justify-content-center align-items-center">
      <h6><b>{{ $t('state-machines.is_loading') }}</b></h6>
      <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
    </div>
    <workflow-chart v-if="!isLoading"
                    :transitions="transitions"
                    :states="states"
                    :stateSemantics="stateSemantics"
                    @state-click="onStateClick($event)"
                    @transition-click="onTransitionClick($event)"
    />
    <br/>
    <div id="state_machine_details">
      <br/>
      <va-card>
        <div>
          <h4>{{ $t('state-machines.state_details') }}</h4>
          <div class="row" v-if="selectedState === null">
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.no_state_selected') }}</span>
            </div>
          </div>
          <div class="row" v-else>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.state.name') }}:</span> {{ selectedState.name }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.state.description') }}:</span> {{ selectedState.description }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.state.created_at') }}:</span> {{ selectedState.created_at }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.state.nr_transitions') }}:</span> {{ selectedState.transitions.length }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.state.message') }}:</span> {{ selectedState.message ? selectedState.message : "N/A" }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.state.success') }}:</span> {{ selectedState.success ? $t(`rules.labels.${selectedState.success}`) : $t('rules.labels.false') }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.state.marketable') }}:</span> {{ selectedState.marketable ? $t(`rules.labels.${selectedState.marketable}`) : $t('rules.labels.false') }}
            </div>
          </div>
        </div>
      </va-card>
      <br/>
      <va-card>
        <div>
          <h4>{{ $t('state-machines.transition_details') }}</h4>
          <div class="row" v-if="selectedTransition === null">
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.no_transition_selected') }}</span>
            </div>
          </div>
          <div class="row" v-else>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.transition.trigger_description') }}:</span>
              {{ selectedTransition.trigger_description ? selectedTransition.trigger_description : "N/A" }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.transition.max_transitions') }}:</span>
              {{ selectedTransition.max_transitions ? selectedTransition.max_transitions : "N/A" }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.transition.created_at') }}:</span> {{
                selectedTransition.created_at
              }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.transition.twilio_call_status') }}:</span> {{
                selectedTransition.twilio_call_status ? selectedTransition.twilio_call_status : "N/A"
              }}
            </div>
            <div class="col-12 col-md-4 mb-3"><span
              class="is-bold ">{{ $t('state-machines.transition.twilio_value') }}:</span> {{
                selectedTransition.twilio_value ? selectedTransition.twilio_value : "N/A"
              }}
            </div>
          </div>
        </div>
      </va-card>
    </div>

  </div>
</template>

<script>
import WorkflowChart from 'vue-workflow-chart'
import LoadingAnimation from '@/components/users/components/Helpers/LoadingAnimation'

export default {
  props: {
    stateMachine: {
      type: Object,
      required: true
    },
    stateMachineStates: {
      type: Array,
      required: true
    }
  },
  components: {
    LoadingAnimation,
    WorkflowChart
  },
  created () {
    this.isLoading = true
    this.states = this.stateMachineStates.map(state => {
      return {
        id: state.id.toString(),
        label: this.processStateName(state.name)
      }
    })

    for (let state of this.stateMachineStates) {
      if (state.marketable && state.marketable === true) {
        this.stateSemantics.push({
          classname: 'marketable',
          id: state.id.toString()
        })
      }
      if (state.success && state.success === true) {
        this.stateSemantics.push({
          classname: 'success',
          id: state.id.toString()
        })
      }
      for (let transition of state.transitions) {
        this.stateMachineTransitions.push(transition)
        let trans = {
          id: `_${transition.id.toString()}`,
          label: transition.max_transitions && transition.max_transitions > 0
            ? this.$t('state-machines.transition.label_max_transitions', { label: transition.trigger_description, max_transitions: transition.max_transitions })
            : transition.trigger_description,
          source: transition.origin_state_id.toString(),
          target: transition.target_state_id.toString(),
        }

        if (transition.max_transitions && transition.max_transitions_target_state) {
          this.transitions.push(
            {
              id: `_${transition.id.toString()}`,
              label: this.$t('state-machines.transition.fallback_transition'),
              source: transition.origin_state_id.toString(),
              target: transition.max_transitions_target_state.toString(),
              stylingClass: 'fallback_trans'
            }
          )
        }

        if (transition.max_transitions && transition.max_transitions > 0) {
          trans.stylingClass = 'max_repetition'
        }

        this.transitions.push(trans)
      }
    }

    this.isLoading = false
  },
  mounted () {
    let chartChildNodes = document.getElementsByClassName('workflow-chart')[0].childNodes
    let biggestTop = 0
    for (let elem of chartChildNodes) {
      if (elem.style.top) {
        biggestTop = elem.style.top.slice(0, -2)
      }
    }

    document.getElementById('state_machine_details').style = `padding-top: ${parseFloat(biggestTop) + 100}px`
  },
  data () {
    return {
      isLoading: false,
      states: [],
      transitions: [],
      selectedState: null,
      selectedTransition: null,
      stateMachineTransitions: [],
      stateSemantics: []
    }
  },
  methods: {
    onStateClick: function ($elem) {
      this.selectedState = this.stateMachineStates.find(state => state.id === parseInt($elem))
      if (this.selectedTransition && this.selectedState && this.selectedTransition.origin_state_id !== this.selectedState.id) {
        this.selectedTransition = null
      }
    },
    onTransitionClick: function ($elem) {
      this.selectedTransition = this.stateMachineTransitions.find(transition => transition.id === parseInt($elem.split('_').pop()))
      if (this.selectedTransition && this.selectedTransition.origin_state_id !== this.selectedState.id) {
        this.selectedState = this.stateMachineStates.find(state => state.id === this.selectedTransition.origin_state_id)
      }
    },
    processStateName: function (name) {
      name = name.replace(/_/g, ' ')
      return name.charAt(0).toUpperCase() + name.slice(1)
    }
  }
}

</script>

<style lang="scss">
@import '~vue-workflow-chart/dist/vue-workflow-chart.css';

.vue-workflow-chart-state {
  border-radius: 30px;
  background-color: #ddddbb;
  font-family: 'PT Sans', sans-serif;
}

.vue-workflow-chart-transition-label {
  background-color: unset !important;
  -webkit-box-shadow: unset !important;
  box-shadow: unset !important;
  font-family: 'PT Sans', sans-serif;
  font-weight: bold !important;
  width: 100px;
  padding-top: 40px;
}

.vue-workflow-chart-transition-arrow {
  opacity: 0.7;
}

.vue-workflow-chart-transition-path {
  opacity: 0.7;
}

.vue-workflow-chart-transition-arrow-max_repetition {
  opacity: 0.7;
  stroke: #c65d09;
  fill: #c65d09;
}

.vue-workflow-chart-transition-path-max_repetition {
  opacity: 0.7;
  stroke: #c65d09;
}

.vue-workflow-chart-transition-arrow-fallback_trans {
  opacity: 0.7;
  stroke: #a70c04;
  fill: #a70c04;
}

.vue-workflow-chart-transition-path-fallback_trans {
  opacity: 0.7;
  stroke: #a70c04;
}

.vue-workflow-chart-transition-arrow-success {
  opacity: 0.7;
  stroke: #4f805d;
  fill: #4f805d;
}

.vue-workflow-chart-transition-path-success {
  opacity: 0.7;
  stroke: #4f805d;
}

.vue-workflow-chart-transition-arrow-marketable {
  opacity: 0.7;
  stroke: #17a2b8;
  fill: #17a2b8;
}

.vue-workflow-chart-transition-path-marketable {
  opacity: 0.7;
  stroke: #17a2b8;
}

.vue-workflow-chart-state-success {
  background-color: #4f805d;
  color: #f5f5f5;
}

.vue-workflow-chart-state-marketable {
  background-color: #17a2b8;
  color: #f5f5f5;
}

</style>
